import { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { adminRegistrationsApi, companyAccountsApi } from '../lib/api';
import { toast } from 'sonner';
import { Building2, User, Stethoscope, CheckCircle2, XCircle, Loader2, Mail, Phone } from 'lucide-react';
import { BrandLoader } from '../components/BrandLoader';

const getErrorMessage = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return 'An error occurred';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join(', ');
  return d.msg || 'An error occurred';
};

function RejectDialog({ open, onClose, onConfirm, name }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject registration</DialogTitle>
          <DialogDescription>
            Optionally provide a reason. <strong>{name}</strong> will be emailed and removed from the pending queue.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional, will be included in the email)..."
            rows={4}
            data-testid="reject-reason-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try { await onConfirm(reason); } finally { setBusy(false); }
            }}
            data-testid="confirm-reject-btn"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Rejecting…</> : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PendingCard({ kind, item, onApprove, onReject, busyId }) {
  const Icon = kind === 'company' ? Building2 : (item.role === 'INVESTIGATOR' ? Stethoscope : User);
  const accent = kind === 'company' ? 'from-blue-600 to-blue-800' : (item.role === 'INVESTIGATOR' ? 'from-violet-600 to-violet-800' : 'from-emerald-600 to-emerald-800');
  const id = kind === 'company' ? `company:${item.id}` : `user:${item.id}`;
  const isBusy = busyId === id;

  return (
    <Card data-testid={`pending-${kind}-${item.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center shrink-0`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-slate-900 truncate">{item.name}</h3>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                {kind === 'company' ? 'Company' : (item.role === 'INVESTIGATOR' ? 'Investigator' : 'Company Rep')}
              </Badge>
            </div>
            <div className="text-sm text-slate-500 flex items-center gap-1 mt-1">
              <Mail className="w-3.5 h-3.5" />
              {kind === 'company' ? item.contact_email : item.email}
            </div>
            {item.phone && (
              <div className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                <Phone className="w-3.5 h-3.5" />
                {item.phone}
              </div>
            )}
            {kind === 'user' && item.role === 'INVESTIGATOR' && (
              <div className="text-sm text-slate-500 mt-1">
                <strong>Specialties:</strong>{' '}
                {item.specialty_names?.length ? item.specialty_names.join(', ') : '—'}
              </div>
            )}
            {kind === 'user' && item.role === 'COMPANY_REP' && item.company_name && (
              <div className="text-sm text-slate-500 mt-1">
                <strong>Company:</strong> {item.company_name}
              </div>
            )}
            {kind === 'user' && item.bio && (
              <div className="text-sm text-slate-600 mt-2 italic">{item.bio}</div>
            )}
            {kind === 'company' && (item.industry || item.website) && (
              <div className="text-sm text-slate-500 mt-1">
                {item.industry && <span><strong>Industry:</strong> {item.industry}</span>}
                {item.industry && item.website && <span> · </span>}
                {item.website && <span><strong>Website:</strong> {item.website}</span>}
              </div>
            )}
            <div className="text-xs text-slate-400 mt-2">
              Submitted {new Date(item.created_at).toLocaleString()}
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={isBusy}
              onClick={() => onApprove(id)}
              data-testid={`approve-${kind}-${item.id}`}
            >
              {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</>}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50"
              disabled={isBusy}
              onClick={() => onReject(id)}
              data-testid={`reject-${kind}-${item.id}`}
            >
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PendingRegistrations({ pending, loading, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null); // { id, name }
  const [filter, setFilter] = useState('all'); // all | company | rep | investigator

  const approve = async (id) => {
    setBusyId(id);
    try {
      const [kind, realId] = id.split(':');
      if (kind === 'company') await companyAccountsApi.approve(realId);
      else await adminRegistrationsApi.approveUser(realId);
      toast.success('Registration approved');
      await onChanged();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusyId(null); }
  };

  const reject = async (reason) => {
    if (!rejectFor) return;
    setBusyId(rejectFor.id);
    try {
      const [kind, realId] = rejectFor.id.split(':');
      if (kind === 'company') await companyAccountsApi.reject(realId, reason || null);
      else await adminRegistrationsApi.rejectUser(realId, reason || null);
      toast.success('Registration rejected');
      setRejectFor(null);
      await onChanged();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusyId(null); }
  };

  const allCompanies = pending?.companies || [];
  const allUsers = pending?.users || [];
  const reps = allUsers.filter(u => u.role === 'COMPANY_REP');
  const investigators = allUsers.filter(u => u.role === 'INVESTIGATOR');

  const companies = filter === 'all' || filter === 'company' ? allCompanies : [];
  const showReps = filter === 'all' || filter === 'rep' ? reps : [];
  const showInvestigators = filter === 'all' || filter === 'investigator' ? investigators : [];
  const filteredCount = companies.length + showReps.length + showInvestigators.length;
  const total = allCompanies.length + allUsers.length;

  if (loading) {
    return (
      <Card><CardContent className="p-12 flex items-center justify-center">
        <BrandLoader size="md" label="Loading pending registrations…" />
      </CardContent></Card>
    );
  }

  if (total === 0) {
    return (
      <Card data-testid="pending-empty">
        <CardContent className="p-12 text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
          <p className="text-slate-700 font-medium">No pending registrations</p>
          <p className="text-slate-500 text-sm mt-1">New self-registrations will appear here for your review.</p>
        </CardContent>
      </Card>
    );
  }

  const FilterChip = ({ value, label, count }) => {
    const active = filter === value;
    return (
      <button
        type="button"
        onClick={() => setFilter(value)}
        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
          active
            ? 'bg-blue-900 text-white shadow-sm'
            : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
        }`}
        data-testid={`pending-filter-${value}`}
      >
        {label}
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
          {count}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-4" data-testid="pending-registrations">
      <div className="flex items-center gap-2 flex-wrap">
        <FilterChip value="all" label="All" count={total} />
        <FilterChip value="company" label="Companies" count={allCompanies.length} />
        <FilterChip value="rep" label="Company Reps" count={reps.length} />
        <FilterChip value="investigator" label="Investigators" count={investigators.length} />
      </div>

      <p className="text-sm text-slate-500">
        Showing {filteredCount} of {total} pending registration{total === 1 ? '' : 's'}.
      </p>

      {filteredCount === 0 && (
        <Card><CardContent className="p-8 text-center text-slate-500 text-sm">
          No pending registrations match this filter.
        </CardContent></Card>
      )}

      {companies.map((c) => (
        <PendingCard
          key={`c-${c.id}`}
          kind="company"
          item={c}
          onApprove={approve}
          onReject={(id) => setRejectFor({ id, name: c.name })}
          busyId={busyId}
        />
      ))}

      {showReps.map((u) => (
        <PendingCard
          key={`u-${u.id}`}
          kind="user"
          item={u}
          onApprove={approve}
          onReject={(id) => setRejectFor({ id, name: u.name })}
          busyId={busyId}
        />
      ))}

      {showInvestigators.map((u) => (
        <PendingCard
          key={`u-${u.id}`}
          kind="user"
          item={u}
          onApprove={approve}
          onReject={(id) => setRejectFor({ id, name: u.name })}
          busyId={busyId}
        />
      ))}

      <RejectDialog
        open={!!rejectFor}
        name={rejectFor?.name || ''}
        onClose={() => setRejectFor(null)}
        onConfirm={reject}
      />
    </div>
  );
}
